import {
	type ActionFunctionArgs,
	type HeadersFunction,
	type LoaderFunctionArgs,
 type MetaFunction,
 json } from '@remix-run/node'
import { Link } from '@remix-run/react'
import {  typedjson, useTypedLoaderData } from 'remix-typedjson'
import { FormieForm, validateForm } from '#app/components/form/index.tsx'
import Header from '#app/components/header.tsx'
import DownloadIcon from '#app/components/icons/download.tsx'
import { createMutationQuery } from '#app/utils/form.server'
import {
	CRAFT_QUERY_KEYS,
	getForm,
	getGraphqlClient,
	getPageDetails,
} from '#app/utils/graphql.server.ts'
import { seomaticMeta } from '#app/utils/misc'
import { getSession, commitSession } from '#app/utils/session.server'
import { type Form as FormType } from '../../../types/graphql.form.ts'

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const noCache = Object.entries(Object.fromEntries(url.searchParams)).filter(
		([key]) => CRAFT_QUERY_KEYS.includes(key),
	).length
	const data = await getPageDetails(request, 'contact-us')
	const { formieForm: form } = await getForm(request, 'contactUs')
	const session = await getSession(request.headers.get('Cookie'))
	// store form in session
	session.set('contact-us', JSON.stringify(form))

	return typedjson(
		{
			form,
			data,
			mediaKit: data.entry.mediaKit[0],
			seo: data.seo,
			subTitle: data.entry.subtitle || '',
			title: data.entry.title || '',
			contactInformation: {
				number: {
					url: data.entry.number?.url,
					text: data.entry.number?.url.split(':')[1],
				},
				email: {
					url: data.entry.email?.url,
					text: data.entry.email?.url.split(':')[1],
				},
				address: data.entry.address,
			},
		},
		{
			headers: {
				'Set-Cookie': await commitSession(session),
				'Cache-Control': noCache
					? 'max-age=0, s-maxage=0'
					: 'max-age=60, s-maxage=300',
			},
		},
	)
}

export let headers: HeadersFunction = ({ loaderHeaders }) => {
	return { 'Cache-Control': loaderHeaders.get('Cache-Control') || '' }
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const session = await getSession(request.headers.get('Cookie'))
	// store form in session
	let form = JSON.parse(session.get('contact-us')) as FormType
	if (!form) {
		form = (await getForm(request, 'contactUs')).formieForm
	}
	let result = await validateForm(form, formData)

	// result.formData.CRAFT_CSRF_TOKEN,
	if (result.ok) {
		const { mutationQuery, mutationData } = await createMutationQuery(
			form,
			formData,
		)
		const client = getGraphqlClient(request)
		const res = await client.request(mutationQuery, mutationData)
		// if (res[form.submissionMutationName].isSpam) {
		// 	result.ok = false
		// 	result.
		// }
		result.data.res = res
	}

	return json(result)
}

export const meta: MetaFunction<
  typeof loader
> = ({ data }: {data: any}) => {
  return seomaticMeta(data.seo)
}

export default function ContactUsRoute() {
	const { contactInformation, form, title, subTitle, mediaKit } =
		useTypedLoaderData<typeof loader>()

	return (
		<>
			<Header subtitle={subTitle} title={title} />
			<main className="container flex flex-col-reverse place-items-start gap-10 divide-y divide-y-reverse divide-copper-100/40 py-20 md:grid md:grid-cols-5 md:gap-y-20 md:divide-x md:divide-y-0">
				<div className="col-span-2 flex h-full flex-col space-y-20 ">
					<div className="flex flex-col space-y-4">
						<h3>Settoon Capital Office</h3>
						<div className="flex flex-col space-y-5">
							<div className="flex flex-row space-x-5">
								<img
									src="/assets/tel.svg"
									alt="telephone"
									className="h-fit w-5 pt-1"
								/>
								<a href={`${contactInformation.number.url}` || '#'}>
									{contactInformation.number.text}
								</a>
							</div>
							<div className="flex flex-row space-x-5">
								<img
									src="/assets/email.svg"
									alt="email"
									className="h-fit w-5 pt-1"
								/>
								<a href={`${contactInformation.email.url}` || '#'}>
									{contactInformation.email.text}
								</a>
							</div>
							<div className="flex flex-row space-x-5">
								<div className="flex items-start  ">
									<img
										src="/assets/address.svg"
										alt="email"
										className="h-fit w-5 pt-2"
									/>
								</div>
								<div
									dangerouslySetInnerHTML={{
										__html: contactInformation?.address || '',
									}}
								></div>
							</div>
						</div>
					</div>
					{mediaKit?.uri && (
						<div className="flex flex-col space-y-5">
							<h4>Download our Media Kit</h4>
							<Link to={mediaKit.uri} className="flex flex-row space-x-2">
								<div>Download</div>
								<DownloadIcon className="h-4 w-4" />
							</Link>
						</div>
					)}
				</div>
				<div className="h-full w-full md:col-span-3">
					<FormieForm
						form={form as FormType}
						className="w-full pb-10 md:pb-0 md:pl-10 lg:pl-[130px]"
					/>
				</div>
			</main>
		</>
	)
}

// export const ErrorBoundary: ErrorBoundaryComponent = ({ error }) => {
// 	console.error(error)
// 	return (
// 		<html>
// 			<head>
// 				<title>Oops!</title>
// 				<Meta />
// 				<Links />
// 			</head>
// 			<body>
// 				<div className="overflow-x-hidden">
// 					<div
// 						id="header"
// 						className="z-0"
// 						style={{
// 							backgroundImage: `url(/assets/background.svg)`,
// 							backgroundRepeat: 'repeat',
// 						}}>
// 						<div className="relative z-30 flex h-full w-full flex-col space-y-[144px] bg-gradient-to-r from-green-100/60 to-green-100/50 px-6 md:px-0">
// 							<header className="container flex w-full flex-col space-y-4 pb-20">
// 								<div className="flex flex-row place-items-center space-x-5">
// 									<h2 className="text-[24px] font-extralight leading-[43px] tracking-wider text-white">
// 										Error
// 									</h2>
// 									<hr className="h-[3px] w-[75px] bg-copper-100" />
// 									{/* <div className="h-0 w-[75px] border-[2px] border-copper-100"></div> */}
// 								</div>
// 								<h1 className="text-[60px] font-light leading-[65px] tracking-wide text-white">
// 									Something Broke
// 								</h1>
// 							</header>
// 						</div>
// 					</div>
// 					<main className="container py-20">
// 						<h3 className="font-serif text-green-300">There was an error.</h3>
// 						<ArrowLink url="/" text="Go to Homepage" type={undefined} />
// 					</main>
// 					<Footer links={[]} />
// 				</div>
// 				<Scripts />
// 			</body>
// 		</html>
// 	)
// }
